import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../primitives/dialog';
import { ActionButton } from '../concept/action-button';
import { Eyebrow } from '../concept/eyebrow';
import {
  BackIcon,
  CameraIcon,
  CheckIcon,
  ChevronForwardIcon,
  SearchIcon,
  SendIcon,
} from '../shell/icons';

export interface NewScanClient {
  id: string;
  name: string;
  initials: string;
  email?: string;
  /** Days since the latest scan, or null for never. Orders the list. */
  daysSinceScan: number | null;
}

type Step = { kind: 'pick' } | { kind: 'how'; id: string } | { kind: 'sent'; id: string; to: string };

/**
 * "New scan", as a choice: which client, then scan them here or send them
 * the link (Carl's start-page proposal, 2026-11).
 *
 * Before this the button went to the client list, and the scan started two
 * screens later from the client's own page. Both routes already existed —
 * the session the client page creates, the email SF-91 sends — so this asks
 * the two questions in one place and calls them.
 *
 * Pure: the page passes the roster and the three actions in. A bottom sheet
 * on a phone, where the coach holds it in one hand; a dialog above that.
 */
export const NewScanSheet: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clients: NewScanClient[];
  /** Open straight on step two for this client, as a queue row does. */
  startWith?: string;
  /** What the person being scanned is called: "client", "customer". */
  noun: { client: string; clients: string };
  onScanHere: (id: string) => Promise<void>;
  /** Resolves with the masked address it went to; throws with copy. */
  onSendLink: (id: string) => Promise<{ to: string }>;
  /** Creates a link without sending it, for when email is not an option. */
  onCopyLink: (id: string) => Promise<string>;
  onNewClient: () => void;
}> = ({ open, onOpenChange, clients, startWith, noun, onScanHere, onSendLink, onCopyLink, onNewClient }) => {
  const [step, setStep] = useState<Step>({ kind: 'pick' });
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState<'scan' | 'send' | 'copy' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Each opening starts clean, on the client a row asked for if it asked.
  useEffect(() => {
    if (!open) return;
    setStep(startWith ? { kind: 'how', id: startWith } : { kind: 'pick' });
    setQuery('');
    setBusy(null);
    setError(null);
    setCopied(false);
  }, [open, startWith]);

  const matches = useMemo(() => {
    const q = fold(query);
    const pool = q ? clients.filter((c) => fold(c.name).includes(q)) : clients;
    // Most recently scanned first: the client in front of the coach today is
    // usually one who was in last week too.
    return [...pool].sort(
      (a, b) =>
        (a.daysSinceScan ?? Number.POSITIVE_INFINITY) - (b.daysSinceScan ?? Number.POSITIVE_INFINITY) ||
        a.name.localeCompare(b.name),
    );
  }, [clients, query]);

  const current = step.kind !== 'pick' ? clients.find((c) => c.id === step.id) : undefined;
  const firstName = current?.name.split(' ')[0] ?? '';

  const run = async (kind: 'scan' | 'send' | 'copy', fn: () => Promise<void>) => {
    setBusy(kind);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : 'Something went wrong. Try again.');
    } finally {
      setBusy(null);
    }
  };

  const copy = (id: string) =>
    run('copy', async () => {
      const url = await onCopyLink(id);
      await navigator.clipboard.writeText(url);
      setCopied(true);
    });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="new-scan-sheet"
        className="flex max-h-[88dvh] flex-col gap-4 bg-card p-4 pb-6 sm:max-w-[440px] sm:rounded-xl sm:p-6 max-sm:bottom-0 max-sm:top-auto max-sm:translate-y-0 max-sm:rounded-t-2xl max-sm:data-[state=closed]:slide-out-to-bottom max-sm:data-[state=open]:slide-in-from-bottom"
      >
        <div aria-hidden="true" className="mx-auto -mt-1 h-1 w-10 rounded-full bg-border sm:hidden" />

        {step.kind === 'pick' && (
          <>
            <div>
              <DialogTitle className="text-lg tracking-[-0.01em]">New scan</DialogTitle>
              <DialogDescription className="mt-1 text-[13px] text-foreground-soft">
                Who are you scanning?
              </DialogDescription>
            </div>
            <label className="flex h-11 shrink-0 items-center gap-2.5 rounded-lg border border-border px-3.5 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary">
              <span className="text-muted-foreground">
                <SearchIcon size={18} />
              </span>
              <input
                data-testid="new-scan-search"
                autoFocus
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Search ${noun.clients}`}
                aria-label={`Search ${noun.clients}`}
                className="h-full min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
            </label>
            <Eyebrow>{query.trim() ? 'Matches' : 'Most recently scanned first'}</Eyebrow>
            <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1">
              {matches.length === 0 ? (
                <p className="py-3 text-sm text-foreground-soft">No {noun.client} matches that search.</p>
              ) : (
                matches.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    data-testid={`new-scan-pick-${c.id}`}
                    onClick={() => setStep({ kind: 'how', id: c.id })}
                    className="flex min-h-[56px] w-full items-center gap-3 border-b border-divider py-2 text-left last:border-b-0 hover:bg-shell"
                  >
                    <Initials text={c.initials} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold">{c.name}</span>
                      <span className="tabular block text-xs text-muted-foreground">{lastScan(c.daysSinceScan)}</span>
                    </span>
                    <span className="text-muted-foreground">
                      <ChevronForwardIcon size={18} />
                    </span>
                  </button>
                ))
              )}
            </div>
            <button
              type="button"
              data-testid="new-scan-new-client"
              onClick={onNewClient}
              className="-mb-1 flex min-h-11 items-center gap-2 self-start text-sm font-semibold text-primary hover:underline"
            >
              + Scan someone new
            </button>
          </>
        )}

        {step.kind === 'how' && current && (
          <>
            <button
              type="button"
              onClick={() => setStep({ kind: 'pick' })}
              className="-my-2 flex min-h-11 items-center gap-1 self-start text-sm font-semibold text-primary"
            >
              <BackIcon size={18} /> {cap(noun.clients)}
            </button>
            <div className="flex items-center gap-3">
              <Initials text={current.initials} />
              <div className="min-w-0">
                <DialogTitle className="truncate text-lg tracking-[-0.01em]">{current.name}</DialogTitle>
                <DialogDescription className="tabular mt-1 text-xs text-muted-foreground">
                  {lastScan(current.daysSinceScan)}
                </DialogDescription>
              </div>
            </div>

            <Choice
              testId="new-scan-here"
              icon={<CameraIcon size={22} />}
              title={busy === 'scan' ? 'Starting…' : 'Scan here, now'}
              disabled={busy !== null}
              onClick={() => run('scan', () => onScanHere(current.id))}
            >
              {firstName} is with you. Opens the camera on this phone and guides you both through the photos.
            </Choice>

            <div className="flex flex-col gap-3 rounded-xl border-2 border-primary bg-accent p-4" data-testid="new-scan-link">
              <div className="flex items-start gap-3.5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  <SendIcon size={20} />
                </span>
                <div>
                  <div className="text-[15px] font-semibold">Send a scan link</div>
                  <p className="mt-0.5 text-[13px] leading-snug text-foreground-soft">
                    {firstName} scans at home with their own phone. The result lands here when they are done.
                  </p>
                </div>
              </div>
              <p className="text-xs text-foreground-soft">
                {current.email ? (
                  <>
                    To <span className="break-all font-medium text-foreground">{current.email}</span>
                  </>
                ) : (
                  'No email on file. Copy the link and send it another way, or add an email in Settings.'
                )}
              </p>
              <div className="flex flex-wrap gap-2">
                {current.email && (
                  <ActionButton
                    variant="primary"
                    testId="new-scan-send"
                    className="min-h-11 flex-1"
                    disabled={busy !== null}
                    onClick={() =>
                      run('send', async () => {
                        const { to } = await onSendLink(current.id);
                        setStep({ kind: 'sent', id: current.id, to });
                      })
                    }
                  >
                    {busy === 'send' ? 'Sending…' : 'Send scan link'}
                  </ActionButton>
                )}
                <ActionButton
                  testId="new-scan-copy"
                  className={`min-h-11 ${current.email ? '' : 'flex-1'}`}
                  disabled={busy !== null}
                  onClick={() => copy(current.id)}
                >
                  {busy === 'copy' ? 'Creating…' : copied ? 'Link copied' : 'Copy link'}
                </ActionButton>
              </div>
            </div>

            {error && (
              <p role="alert" data-testid="new-scan-error" className="text-[13px] font-medium text-destructive">
                {error}
              </p>
            )}
            <p className="text-xs text-muted-foreground">A scan link works for 24 hours.</p>
          </>
        )}

        {step.kind === 'sent' && current && (
          <div data-testid="new-scan-sent" className="flex flex-col items-center gap-3 pt-2 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent text-primary">
              <CheckIcon size={26} />
            </span>
            <DialogTitle className="text-lg">Scan link sent to {firstName}</DialogTitle>
            <DialogDescription className="max-w-[320px] text-sm leading-relaxed text-foreground-soft">
              Sent to {step.to}. It works for 24 hours, and the scan shows up on your overview as soon as{' '}
              {firstName} is done.
            </DialogDescription>
            <ActionButton variant="primary" className="mt-2 min-h-11 w-full" onClick={() => onOpenChange(false)}>
              Done
            </ActionButton>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Choice: React.FC<{
  icon: React.ReactNode;
  title: string;
  disabled?: boolean;
  onClick: () => void;
  testId?: string;
  children: React.ReactNode;
}> = ({ icon, title, disabled, onClick, testId, children }) => (
  <button
    type="button"
    data-testid={testId}
    disabled={disabled}
    onClick={onClick}
    className="flex items-start gap-3.5 rounded-xl border border-border p-4 text-left transition-colors hover:border-primary disabled:opacity-60"
  >
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground">
      {icon}
    </span>
    <span>
      <span className="block text-[15px] font-semibold">{title}</span>
      <span className="mt-0.5 block text-[13px] leading-snug text-foreground-soft">{children}</span>
    </span>
  </button>
);

const Initials: React.FC<{ text: string }> = ({ text }) => (
  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-semibold text-accent-foreground">
    {text || '—'}
  </span>
);

const fold = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const lastScan = (days: number | null) =>
  days === null
    ? 'Never scanned'
    : days <= 0
      ? 'Last scan today'
      : days === 1
        ? 'Last scan yesterday'
        : `Last scan ${days} days ago`;
